// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'TagGroup' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tag} from '@wandb/weave/components/Tag';
import React, {useCallback} from 'react';

import {trackPanelBankEvent} from '../../../../services/analytics/panelBankEvents';
import {usePanelSearchDebouncedContext} from '../../../../state/panelbank/PanelSearchContextProvider';
import {useViewAction} from '../../../../state/views/hooks';
import {pinSection} from '../../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigTypes from '../../../../state/views/panelBankSectionConfig/types';
import {TruncateTextTooltip} from '../../../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../../../common/TruncateText/TruncateTextTypes';
import {EditableLabel} from '../../../elements/EditableLabel';
import {useIsSectionPinned} from '../../../SettingControls/useIsSectionPinned';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';

export const PanelBankSectionTitle = ({
  activePanelCount,
  isEditingName,
  isSearching,
  isSectionNameSearchMatch,
  panelBankSectionConfigRef,
  panelSearchMatchCount,
  sectionName,
  setIsEditingName,
  updateName,
}: {
  activePanelCount: number;
  isEditingName: boolean;
  isSearching: boolean;
  isSectionNameSearchMatch: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  panelSearchMatchCount: number;
  sectionName: string;
  setIsEditingName: (editing: boolean) => void;
  updateName: (text: string) => void;
}) => {
  const {makeHighlightSearchTitle} = usePanelSearchDebouncedContext();

  if (isEditingName) {
    return (
      <span className="panel-bank__section-title-content">
        <EditableLabel
          title={sectionName}
          onSave={updateName}
          onBlur={() => setIsEditingName(false)}
          editing={isEditingName}
          serverText={sectionName}
          onClick={(e: React.SyntheticEvent) => {
            // we use the menu option to trigger editing mode, rather than direct click
            e.preventDefault();
          }}
          dataTest={'panel-bank-section-title-label'}
        />
        <span data-test="panel-bank-section-panel-count">
          <PanelBankSectionCountLabel
            isSectionNameSearchMatch={isSectionNameSearchMatch}
            panelSearchMatchCount={panelSearchMatchCount}
            activePanelCount={activePanelCount}
            isSearching={isSearching}
          />
        </span>
        <PanelBankSectionPinnedTag
          panelBankSectionConfigRef={panelBankSectionConfigRef}
        />
      </span>
    );
  }

  return (
    <span className="panel-bank__section-title-content">
      <TruncateTextTooltip
        truncationType={TruncationType.End}
        className="text-left"
        noLink
        fullText={sectionName}
        highlight={makeHighlightSearchTitle}
        dataTest={'panel-bank-section-title-label'}
      />
      <PanelBankSectionCountLabel
        isSectionNameSearchMatch={isSectionNameSearchMatch}
        panelSearchMatchCount={panelSearchMatchCount}
        activePanelCount={activePanelCount}
        isSearching={isSearching}
      />
      <PanelBankSectionPinnedTag
        panelBankSectionConfigRef={panelBankSectionConfigRef}
      />
    </span>
  );
};

const PanelBankSectionCountLabel = ({
  isSectionNameSearchMatch,
  panelSearchMatchCount,
  activePanelCount,
  isSearching,
}: {
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  activePanelCount: number;
  isSearching: boolean;
}) => {
  const labelText =
    isSearching && !isSectionNameSearchMatch
      ? `${panelSearchMatchCount.toLocaleString()} of ${activePanelCount.toLocaleString()}`
      : activePanelCount.toLocaleString();

  return (
    <span data-test="panel-bank-section-panel-count">
      <Tag color="moon" label={labelText} />
    </span>
  );
};

const PanelBankSectionPinnedTag = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const pinPanelBankSection = useViewAction(panelBankConfigRef, pinSection);
  const isPinned = useIsSectionPinned(panelBankSectionConfigRef);

  const onRemove = useCallback(
    (e: React.SyntheticEvent) => {
      pinPanelBankSection?.(panelBankSectionConfigRef);
      // this button always unpins a section
      trackPanelBankEvent({action: 'Unpin panel bank section'});
      e.stopPropagation();
    },
    [panelBankSectionConfigRef, pinPanelBankSection]
  );

  if (!isPinned) {
    return null;
  }

  return (
    <div
      onClick={onRemove}
      className="flex h-22 w-[86px] items-center rounded-sm bg-moon-300 px-3 py-6 text-sm text-moon-600 hover:bg-teal-300 hover:text-teal-600">
      <Icon className="mr-4 h-14 w-14" name="pin" />
      <span className="mr-4">Pinned</span>
      <Icon className="mr-4 h-14 w-14" name="close" />
    </div>
  );
};
